import { faFile } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AlertModal,
  Box,
  Checkbox,
  ConfirmationModal,
  ContextMenu,
  ContextMenuItem,
  ContextMenuList,
  ContextMenuTrigger,
  ErrorModal,
  Flex,
  ListItem,
  SquareButton,
  Text,
  ThreeDotsIcon,
  Tooltip,
  useConfirmModal,
  useOpenModal,
} from '@modusign/design-system';
import { SystemStyleObject } from '@styled-system/css';
import FileSaver from 'file-saver';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useToggle } from 'react-use';

import isIE from '@libs/isIE';

import BlockIEModal from '@app/components/BlockIEModal';
import FormatTimeText from '@app/components/FormatTimeText';
import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import { ContractSetupStartConfirmModalProps } from '@app/containers/Modal/ContractSetupStartConfirmModal';
import SigningRequestFeatureGuideModal from '@app/containers/Modal/SigningRequestFeatureGuideModal';
import {
  CreateLinkErrorModal,
  useStartContractLinkByDraft,
} from '@app/features/contractLink/contractLinkCreate';
import { useDocumentPermission } from '@app/features/documents/hooks/useDocumentPermission';
import { convertDraftToPdf } from '@app/features/form';
import DeleteDraftsModal from '@app/features/form/components/DeleteDraftsModal';
import { useDraftPermission } from '@app/features/form/hooks/useDraftPermission';
import { useCreateTemplateAvailability } from '@app/features/template';
import useTemplatePermission from '@app/features/template/hooks/useTemplatePermission';
import useContractSetupDocumentFileUpload from '@app/hooks/documentSetup/useContractSetupDocumentFileUpload';
import useTemplateDocumentFileUpload from '@app/hooks/documentSetup/useTemplateDocumentFileUpload';
import useUploadEditingDocumentFile from '@app/hooks/editingDocument/useUploadEditingDocumentFile';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import coalesceGlobalErrorMessage from '@app/libs/coalesceGlobalErrorMessage';
import { recordGTMEvent } from '@app/store/analytics/actions';
import { editingDocumentSetIsNewEditingDocument } from '@app/store/editingDocument/actions';
import { modalOpen } from '@app/store/modal/actions';
import { Dispatch } from '@app/store/types';

import DraftsDropdown from '../../DraftsDropdown';

interface DraftListItemProps {
  id: string;
  isChecked: boolean;
  isOpen: boolean;
  disabled: boolean;
  name: string;
  createdAt: string;
  updatedAt: string;
  onCheckToggle: () => void;
  onCopy: (draftId: string) => void;
  onToggleDropdown: () => void;
}

export default function DraftListItem({
  id,
  isChecked,
  isOpen,
  disabled,
  name,
  createdAt,
  updatedAt,
  onCheckToggle,
  onCopy,
  onToggleDropdown,
}: DraftListItemProps) {
  const dispatch = useDispatch<Dispatch>();
  const history = useHistory();

  const openModal = useOpenModal();

  const { editablePermission } = useDraftPermission({ draftId: id });
  const { signingRequestablePermission } = useDocumentPermission();
  const { createTemplatePermission } = useTemplatePermission();
  const { templateLimit } = useCreateTemplateAvailability();

  const [isRequestSigningWarningModalOpen, toggleRequestSigningWarningModalOpen] = useToggle(false);
  const [isTemplateLimitErrorModalOpen, toggleTemplateLimitErrorModalOpen] = useToggle(false);

  const [isBlockIeModalOpen, toggleBlockIeModal] = useToggle(false);
  const [convertPdfErrorMessage, setConvertPdfErrorMessage] = useState<string | null>(null);
  const [isContextMenuOpen, setContextMenuOpen] = useState(false);

  const { isLoading: isContractSetupDocumentUploading, mutate: uploadContractSetupDocumentFile } =
    useContractSetupDocumentFileUpload('drafts');

  const { isLoading: isTemplateDocumentUploading, mutate: uploadTemplateDocumentFile } =
    useTemplateDocumentFileUpload('drafts');

  const workspaceId = useWorkspaceId();
  const onRequestDocumentClick = async () => {
    dispatch(recordGTMEvent('draftSigningRequestClick'));

    if (!signingRequestablePermission.isPermitted) {
      toggleRequestSigningWarningModalOpen();
      return;
    }

    const resultAction = await dispatch(convertDraftToPdf({ workspaceId, draftId: id }));
    if (convertDraftToPdf.fulfilled.match(resultAction)) {
      const blob = resultAction.payload;
      uploadContractSetupDocumentFile(
        {
          name: `${name}.pdf`,
          blob,
        },
        {
          onSuccess: ({ fileId, url, title }) => {
            dispatch(
              modalOpen('CONTRACT_SETUP_START_CONFIRM_MODAL', {
                fileId,
                startFrom: 'drafts',
                title,
                url,
                draftId: id,
              } as ContractSetupStartConfirmModalProps)
            );
          },
        }
      );
    } else if (convertDraftToPdf.rejected.match(resultAction)) {
      setConvertPdfErrorMessage(coalesceGlobalErrorMessage(resultAction.payload?.message || ''));
    }
  };

  const onCreateTemplateClick = async () => {
    dispatch(recordGTMEvent('draftCreateTemplateClick'));

    if (!createTemplatePermission.isPermitted) {
      toggleTemplateLimitErrorModalOpen();
      return;
    }

    const resultAction = await dispatch(convertDraftToPdf({ workspaceId, draftId: id }));
    if (convertDraftToPdf.fulfilled.match(resultAction)) {
      const blob = resultAction.payload;
      uploadTemplateDocumentFile({
        name: `${name}.pdf`,
        blob,
      });
    } else if (convertDraftToPdf.rejected.match(resultAction)) {
      setConvertPdfErrorMessage(coalesceGlobalErrorMessage(resultAction.payload?.message || ''));
    }
  };

  const {
    mutate: handleConvertToContractLink,
    error,
    isLoading: isContractLinkConverting,
    reset,
  } = useStartContractLinkByDraft();

  const { isLoading: isEditingDocumentUploading, mutate: uploadEditingDocumentFile } =
    useUploadEditingDocumentFile({
      onError: (error) => {
        dispatch(modalOpen('FILE_UPLOAD_ERROR_MODAL', { error }));
      },
      onSuccess: ({ editingDocumentId }) => {
        history.push('/editor/single', {
          editingDocumentId,
        });
        dispatch(editingDocumentSetIsNewEditingDocument());
      },
    });

  const isLoading =
    isContractSetupDocumentUploading ||
    isTemplateDocumentUploading ||
    isContractLinkConverting ||
    isEditingDocumentUploading;

  const onAddSignatureClick = async () => {
    dispatch(recordGTMEvent('draftSignatureClick'));

    if (!(await openAddSignatureModal())) {
      return;
    }

    const resultAction = await dispatch(convertDraftToPdf({ workspaceId, draftId: id }));
    if (convertDraftToPdf.fulfilled.match(resultAction)) {
      const blob = resultAction.payload;
      uploadEditingDocumentFile({
        name: `${name}.pdf`,
        blob,
      });
    } else if (convertDraftToPdf.rejected.match(resultAction)) {
      setConvertPdfErrorMessage(coalesceGlobalErrorMessage(resultAction.payload?.message || ''));
    }
  };

  const onDownloadPdfClick = async () => {
    dispatch(recordGTMEvent('draftDownloadClick'));

    const resultAction = await dispatch(convertDraftToPdf({ workspaceId, draftId: id }));
    if (convertDraftToPdf.fulfilled.match(resultAction)) {
      const blob = resultAction.payload;
      FileSaver.saveAs(blob, `${name}.pdf`);
    } else if (convertDraftToPdf.rejected.match(resultAction)) {
      setConvertPdfErrorMessage(coalesceGlobalErrorMessage(resultAction.payload?.message || ''));
    }
  };

  const onEditDraftClick = useCallback(() => {
    dispatch(recordGTMEvent('draftEditClick'));
    if (isIE()) {
      toggleBlockIeModal();
    } else {
      history.push(`/draft/${id}`);
    }
  }, [dispatch, history, id, toggleBlockIeModal]);

  const handleDraftCopy = useCallback(() => {
    onCopy(id);
  }, [id, onCopy]);

  const { modalProps: addSignatureModalProps, openModal: openAddSignatureModal } =
    useConfirmModal();

  const itemSx: SystemStyleObject = isOpen
    ? {
        borderColor: 'tableBorderNeutralBase',
        boxShadow: '0px 20px 40px -20px rgba(0, 0, 0, 0.2)',
      }
    : {
        ':hover': {
          bg: '#fafafa',
        },
        borderBottomColor: 'tableBorderNeutralBase',
        cursor: disabled ? 'not-allowed' : undefined,
      };

  return (
    <Box position="relative">
      <Flex
        borderColor="transparent"
        borderStyle="solid"
        borderWidth="0px 1px 1px 1px"
        height={[null, '72px']}
        pl={[4, 0]}
        py={[5, 0]}
        role="row"
        sx={itemSx}
      >
        <Flex alignItems="stretch" flex="1 1 auto" flexDirection={['column', 'row']}>
          <Box
            alignItems="center"
            display={['none', 'flex']}
            flex="0 0 auto"
            justifyContent="center"
            role="cell"
            width="50px"
          >
            <Checkbox checked={isChecked} disabled={disabled} onClick={onCheckToggle} />
          </Box>
          <Box
            as="button"
            disabled={disabled}
            flexGrow={[null, 1]}
            overflow="hidden"
            role="cell"
            sx={{
              textOverflow: 'ellipsis',
              '&:disabled': {
                color: 'textNeutralUltraLight',
              },
            }}
            textAlign="left"
            whiteSpace="nowrap"
            onClick={onToggleDropdown}
          >
            <FontAwesomeIcon icon={faFile} style={{ marginRight: '4px' }} />
            {name}
          </Box>
          <Box
            as="button"
            disabled={disabled}
            display={['none', 'block']}
            flex="0 0 auto"
            role="cell"
            width="120px"
            onClick={onToggleDropdown}
          >
            <FormatTimeText dateString={createdAt} />
          </Box>
          <Box
            as="button"
            disabled={disabled}
            flex="0 0 auto"
            mt={[2, 0]}
            role="cell"
            textAlign={['left', 'center']}
            width="120px"
            onClick={onToggleDropdown}
          >
            <FormatTimeText dateString={updatedAt} />
          </Box>
        </Flex>
        <Flex
          alignItems="center"
          flex="0 0 auto"
          justifyContent="center"
          role="cell"
          textAlign={['left', 'center']}
          width={['80px', null, '120px']}
        >
          <ContextMenu isOpen={isContextMenuOpen} onOpenChange={setContextMenuOpen}>
            <ContextMenuTrigger>
              <SquareButton
                aria-label="추가메뉴 토글하기"
                disabled={disabled}
                onClick={() => setContextMenuOpen((x) => !x)}
              >
                <ThreeDotsIcon />
              </SquareButton>
            </ContextMenuTrigger>
            <ContextMenuList minWidth="160px" top="36px">
              <ContextMenuItem onClick={onDownloadPdfClick}>PDF 다운로드</ContextMenuItem>
              <Tooltip
                content={editablePermission.message}
                disabled={editablePermission.isPermitted}
                placement="left"
                scale="sm"
              >
                <Box>
                  <ContextMenuItem
                    disabled={!editablePermission.isPermitted}
                    onClick={() =>
                      openModal((modalProps) => (
                        <DeleteDraftsModal
                          {...modalProps}
                          draftIds={[id]}
                          onConfirm={() => {
                            if (isChecked) {
                              onCheckToggle();
                            }
                          }}
                        />
                      ))
                    }
                  >
                    삭제
                  </ContextMenuItem>
                </Box>
              </Tooltip>
            </ContextMenuList>
          </ContextMenu>
        </Flex>
      </Flex>
      <DraftsDropdown
        draftId={id}
        isOpen={isOpen}
        onAddSignatureClick={onAddSignatureClick}
        onConvertToContractLinkClick={() => handleConvertToContractLink({ id, title: name })}
        onCreateTemplateClick={onCreateTemplateClick}
        onDraftCopy={handleDraftCopy}
        onEditDraftClick={onEditDraftClick}
        onRequestDocumentClick={onRequestDocumentClick}
        onToggleDropdown={onToggleDropdown}
      />
      <ConfirmationModal
        {...addSignatureModalProps}
        confirmButtonLabel="문서편집기로 이동"
        maxWidth="xs"
        title="내 사진/도장 넣기 안내"
        variant="warning"
      >
        <Text textAlign="center">사인/도장을 넣으려면 문서편집기 이용이 필요합니다.</Text>
        <Box
          as="ul"
          bg="boxBackgroundNeutralBase"
          border="1px solid"
          borderColor="boxBorderNeutralBase"
          mt={7}
          px={6}
          py={7}
        >
          <ListItem variant="circle">문서편집기를 이용하면, 서식이 PDF로 변환됩니다.</ListItem>
          <ListItem mt={2} variant="circle">
            PDF로 편집한 서식은 문서편집기 목록에 저장됩니다.
          </ListItem>
        </Box>
      </ConfirmationModal>
      <ErrorModal
        error={convertPdfErrorMessage}
        isOpen
        title="서식 변환 실패"
        onClose={() => setConvertPdfErrorMessage(null)}
      />
      <SigningRequestFeatureGuideModal
        isOpen={isRequestSigningWarningModalOpen}
        onClose={toggleRequestSigningWarningModalOpen}
      />
      <AlertModal
        isOpen={isTemplateLimitErrorModalOpen}
        maxWidth="xxs"
        title="새 템플릿 만들기"
        variant="warning"
        onClose={toggleTemplateLimitErrorModalOpen}
      >
        템플릿 사용 개수({templateLimit}개)를 초과할 수 없습니다.
        <br />
        템플릿이 {templateLimit}개 이하가 되도록
        <br />
        삭제 후 다시 시도해 주세요.
      </AlertModal>
      <BlockIEModal
        isOpen={isBlockIeModalOpen}
        title="서식 작성 안내"
        onConfirm={toggleBlockIeModal}
      />
      <CreateLinkErrorModal error={error} onConfirm={reset} />
      {isLoading && <GlobalLoadingSpinner />}
    </Box>
  );
}
